/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the built-in sample spec.

   RESTORED (Josh, 2026-08-13): this is the CLASSIC master forma editor's
   sample presentation (CanvasComponents.tsx's `LevelRow` + utils.ts's
   `getSamples`, both retired in commit f06b570 when the classic canvas was
   deleted) — one independent pair of rows PER DEFINED LEVEL, in level
   order, each level's own text ("This is what your level N text will look
   like", identical both rows). NOT a nested CSI narrative — an earlier
   attempt this same run built one of those and Josh rejected it: "put the
   sample body back to how it was."

   Two rows per level was ALREADY the classic law (`LevelRow`'s own header
   comment: "renders 2 sample rows per level") and remains Josh's standing
   law, restated 2026-08-12/13: "each level needs to have two paragraphs
   shown because sometimes spacing needs to happen between levels of the
   same kind." Two SIBLING paragraphs back to back — no other level
   between them — is the only way to see space-before/after and
   contextual spacing behave between paragraphs of the SAME level, as
   opposed to a level transition.

   Unlike the classic canvas (which hand-computed each level's numbering
   independently via `getSamples`, never actually nesting one level under
   another), this sample is ONE FLAT SEQUENCE — level 1's pair, then level
   2's pair, … up through the deepest level the forma defines — run
   through the SAME `shared/body-layout.ts` brain the export uses
   (computeNumbering + resolveBodyLayout + bodyParagraphCss), so the
   numbers on screen are REAL, not fabricated. Because each level only
   ever follows a SHALLOWER level in this sequence, computeNumbering's
   real counters reset to 1 at the start of every level exactly like
   classic's fixed ['A.','B.'], ['1.','2.'], etc. did — same visual
   result, WYSIWYG-true source (contract §3 R6: "what the Studio shows is
   what word-engine would print for this content").

   Every level the forma defines renders (levels 1..N, N = the number of
   entries in the forma's own `levels` array — see `sampleMaxLevel`), so a
   forma that styles level 5+ (every real forma does; `levels` always
   carries all nine) is no longer invisible past level 4 the way the
   classic canvas's OWN fixed CSI-heading-only presentation never was
   either — classic always rendered every one of the 9 rows.
   ═══════════════════════════════════════════════════════════════ */

import type { ContentBlock, StandardConfig } from '../../shared/types';

/** Clamp to the engine's 1-9 level range. No forma / no levels array yet
 *  (a brand-new, not-yet-saved forma) falls back to the full 9 — the same
 *  range `resolveBodyLayout` always resolves. */
export function sampleMaxLevel(forma: StandardConfig | null | undefined): number {
  const n = forma?.levels?.length;
  if (!Number.isFinite(n) || !n) return 9;
  return Math.max(1, Math.min(9, Math.trunc(n as number)));
}

/**
 * Build the Studio's sample body for the open forma. Pure — same input,
 * same output — so a caller can `useMemo` on the forma reference alone and
 * stay live as the draft changes (no stale snapshot of the level count).
 */
export function generateStudioSampleBlocks(
  forma: StandardConfig | null | undefined
): ContentBlock[] {
  const n = sampleMaxLevel(forma);
  let seq = 0;
  const blk = (level: number, text: string): ContentBlock => ({ id: `s${++seq}`, level, text });
  const out: ContentBlock[] = [];

  for (let lvl = 1; lvl <= n; lvl++) {
    const text = `This is what your level ${lvl} text will look like`;
    out.push(blk(lvl, text));
    out.push(blk(lvl, text));
  }

  return out;
}

export const STUDIO_SAMPLE_SPEC_NUMBER = '07 21 00';
export const STUDIO_SAMPLE_SPEC_NAME = 'Thermal Insulation';
